import { useEffect, useState } from 'react'
import { Gauge } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { hentKvote, type Kvote } from './oppslagApi'

interface Props {
  /** Passed in after a lookup so the badge updates without a second request —
   *  every Oppslag response already carries the fresh quota. */
  kvote?: Kvote | null
  /** Which budget this tab spends from. Three separate ledgers, because they
   *  are three separate agreements — and Brreg publishes no ceiling of its
   *  own, so ours is the only one. */
  api: 'teknisk' | 'maskinporten' | 'brreg'
}

/**
 * Remaining daily lookups, shown on every tab.
 *
 * Deliberately always visible rather than only on error: the point is that a
 * user sees the ceiling BEFORE starting a 100-plate batch, not after being
 * refused halfway through.
 */
export default function KvoteBadge({ kvote, api }: Props) {
  const { t } = useTranslation('oppslag')
  const [hentet, setHentet] = useState<Kvote | null>(null)

  useEffect(() => {
    if (kvote) return
    hentKvote()
      .then(setHentet)
      .catch(() => setHentet(null))
  }, [kvote])

  const aktiv = kvote ?? hentet
  if (!aktiv) return null

  const status = aktiv[api]
  const andel = status.budsjett > 0 ? status.gjenstaende / status.budsjett : 0
  const tone =
    status.gjenstaende === 0
      ? 'text-red-700 bg-red-50 dark:text-red-300 dark:bg-red-900/30'
      : andel < 0.2
        ? 'text-amber-700 bg-amber-50 dark:text-amber-300 dark:bg-amber-900/30'
        : 'text-gray-600 bg-gray-50 dark:text-gray-300 dark:bg-gray-800'

  return (
    <span
      data-testid="oppslag-kvote"
      className={`inline-flex items-center gap-1.5 rounded-sm px-2 py-1 text-xs font-medium ${tone}`}
      title={t('kvote.title', { dato: aktiv.dato })}
    >
      <Gauge className="w-3.5 h-3.5" />
      {t('kvote.remaining', { gjenstaende: status.gjenstaende, budsjett: status.budsjett })}
    </span>
  )
}
